/**
 * Edit User Page
 *
 * Purpose: Edit user details
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { editUserSchema, type EditUserFormData } from '../models/edit_user_form'
import {
  mapUserToFormDefaults,
  useEditUserViewModel,
} from '../viewmodels/use_edit_user_viewmodel'

export function EditUserPageView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [phoneCleared, setPhoneCleared] = useState(false)
  const { state, isLoading, error, actions } = useEditUserViewModel(id)
  const userData = state.user

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    reset,
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    shouldUnregister: false,
    shouldUseNativeValidation: false,
  })

  const selectedImage = watch('image') as FileList | undefined

  useEffect(() => {
    if (!userData) return
    reset(mapUserToFormDefaults(userData))
    if (userData.imageUrl) {
      setImagePreview(userData.imageUrl)
    }
  }, [userData, reset])

  // Set image preview when file is selected
  useEffect(() => {
    if (selectedImage && selectedImage.length > 0) {
      const file = selectedImage[0]
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setImagePreview(reader.result as string)
          setImageError(false)
        }
        reader.readAsDataURL(file)
      }
    } else if (userData?.imageUrl) {
      setImagePreview(userData.imageUrl)
      setImageError(false)
    } else {
      setImagePreview(null)
      setImageError(true)
    }
  }, [selectedImage, userData?.imageUrl])

  const onSubmit = async (data: EditUserFormData) => {
    const result = await actions.submitUpdate(data)
    if (result.ok) {
      navigate('/admin/users')
      return
    }
    alert(result.message)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">Update User</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error || !userData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">Update User</h1>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-red-600 font-medium mb-2">Failed to load user data</p>
          <p className="text-sm text-gray-500">{error || 'User not found'}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Back to Users
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-800">Update User</h1>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
          </div>

          {/* Phone No and Date Of Birth */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Phone No */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone No <span className="text-red-500">*</span>
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value, ...field } }) => {
                  // If phone was manually cleared, use undefined. Otherwise use the value or undefined if empty
                  const phoneValue =
                    phoneCleared && (!value || value.trim() === '')
                      ? undefined
                      : value && value.trim() !== ''
                        ? value
                        : undefined

                  return (
                    <PhoneInput
                      {...field}
                      value={phoneValue}
                      onChange={val => {
                        // Track if user manually cleared the field
                        if (val === undefined || val === null || val === '') {
                          setPhoneCleared(true)
                          onChange('')
                        } else {
                          setPhoneCleared(false)
                          onChange(val)
                        }
                      }}
                      international
                      defaultCountry="US"
                      className="phone-input-wrapper"
                    />
                  )
                }}
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
            </div>

            {/* Date Of Birth */}
            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                Date Of Birth <span className="text-red-500">*</span>
              </label>
              <input
                id="dateOfBirth"
                type="date"
                {...register('dateOfBirth')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {errors.dateOfBirth && (
                <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth.message}</p>
              )}
            </div>
          </div>

          {/* Image and Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Image */}
            <div>
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                Image
              </label>
              <div className="space-y-2">
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  {...register('image')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                {(['male', 'female', 'other'] as const).map(gender => (
                  <label key={gender} className="flex items-center">
                    <input
                      type="radio"
                      value={gender}
                      {...register('gender')}
                      className="mr-2 text-teal-500 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">{gender}</span>
                  </label>
                ))}
              </div>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
              )}
            </div>
          </div>

          {/* Image Preview and Bio */}
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4">
            {/* Image Preview */}
            <div className="w-fit">
              <div>
                {imagePreview && !imageError ? (
                  <img
                    src={imagePreview}
                    alt="Profile preview"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    onError={() => {
                      setImageError(true)
                    }}
                  />
                ) : (
                  <div className="w-32 h-32 rounded-lg border border-gray-300 bg-gray-200 flex items-center justify-center text-4xl font-semibold text-gray-500">
                    {userData?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              {/* Active checkbox below image */}
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('active')}
                    className="mr-2 w-4 h-4 text-teal-500 focus:ring-teal-500 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                Bio <span className="text-red-500">*</span>
              </label>
              <textarea
                id="bio"
                rows={4}
                {...register('bio')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {errors.bio && <p className="mt-1 text-sm text-red-600">{errors.bio.message}</p>}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#019b9a' }}
            >
              {isSubmitting ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
